import React, { useEffect, useState } from 'react';
import {
  Button, Col, DatePicker, Divider, Form, Input, Modal, Row,
  Select, Spin, Switch, Table, Tag, Typography,
} from 'antd';
import dayjs from 'dayjs';
import api from '../api';

const STATUS_COLOR = { draft: 'default', in_progress: 'blue', complete: 'green', inactive: 'gold', canceled: 'red' };
const STATUS_LABEL = { draft: 'Draft', in_progress: 'In Progress', complete: 'Complete', inactive: 'Inactive', canceled: 'Canceled' };
// Same list as the full incident page.
const WARNING_OPTIONS = [
  { label: '1st Warning',   value: '1st' },
  { label: '2nd Warning',   value: '2nd' },
  { label: '3rd Warning',   value: '3rd' },
  { label: 'Final Warning', value: 'Final' },
];

export default function ComplianceCreateModal({
  open,
  agentUserId,
  agentName,
  initialDate,
  initialDescription,
  initialTeamId,
  initialTeamName,
  currentUser,
  navigate,
  sourceType,
  sourceId,
  onClose,
  onCreated,
  onCreatedAndOpen,
}) {
  const [form]           = Form.useForm();

  // Expiry dates default to 30 days after the incident date (or today, when
  // there is none yet) the moment a warning is picked or QA Related turned on.
  // Only an empty date is filled, so a date someone chose is never overwritten.
  const defaultExpiries = (changed) => {
    const base = form.getFieldValue('date_of_incident');
    const def = (base ? dayjs(base) : dayjs()).add(30, 'day');
    if (changed.warning_number && !form.getFieldValue('warning_expires_on')) {
      form.setFieldValue('warning_expires_on', def);
    }
    if (changed.is_qa_related && !form.getFieldValue('qa_related_expires_on')) {
      form.setFieldValue('qa_related_expires_on', def);
    }
  };
  const [saving,         setSaving]         = useState(false);
  const [issueTypes,     setIssueTypes]     = useState([]);
  const [users,          setUsers]          = useState([]);
  const [teams,          setTeams]          = useState([]);
  const [history,        setHistory]        = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState(null);

  const loadAssignUsers = (teamId) => {
    const params = { can_edit_incidents: 1 };
    if (teamId) params.team_id = teamId;
    api.get('/api/users', { params }).then((r) => setUsers(r.data)).catch(() => {});
  };

  // Load dropdowns and history when the modal opens
  useEffect(() => {
    if (!open || !agentUserId) return;

    const teamId = initialTeamId || null;
    setSelectedTeamId(teamId);

    if (issueTypes.length === 0)
      api.get('/api/compliance/issues?active_only=1').then((r) => setIssueTypes(r.data)).catch(() => {});
    loadAssignUsers(teamId);
    if (teams.length === 0)
      api.get('/api/teams').then((r) => setTeams(r.data)).catch(() => {});

    setHistory([]);
    setHistoryLoading(true);
    const dateFrom = dayjs().subtract(90, 'day').format('YYYY-MM-DD');
    api.get('/api/compliance', { params: { employee_user_id: agentUserId, type: 'incident', date_from: dateFrom } })
      .then(res => {
        setHistory([...res.data].sort((a, b) =>
          (b.DateOfIncident || b.CreatedAt).localeCompare(a.DateOfIncident || a.CreatedAt)));
      })
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [open, agentUserId]); // eslint-disable-line

  // Pre-fill form whenever the modal opens
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    let teamId = initialTeamId || null;
    if (!teamId && initialTeamName && teams.length) {
      teamId = teams.find((t) => t.Name === initialTeamName)?.ID || null;
    }
    form.setFieldsValue({
      date_of_incident:    initialDate ? dayjs(initialDate) : dayjs(),
      team_id:             teamId,
      assigned_to_user_id: currentUser?.ID || null,
      description:         initialDescription ?? '',
      is_qa_related:       false,
    });
  }, [open]); // eslint-disable-line

  const handleCreate = async (andOpen = false) => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const body = {
        type:                'incident',
        employee_user_id:    agentUserId,
        date_of_incident:    values.date_of_incident?.format('YYYY-MM-DD'),
        description:         values.description || null,
        issue_ids:           values.issue_ids || [],
        status:              andOpen ? 'draft' : 'complete',
        team_id:             values.team_id || null,
        assigned_to_user_id: values.assigned_to_user_id || null,
        warning_number:      values.warning_number || null,
        warning_expires_on:  values.warning_number ? (values.warning_expires_on?.format('YYYY-MM-DD') || null) : null,
        is_qa_related:       !!values.is_qa_related,
        qa_related_expires_on: values.is_qa_related ? (values.qa_related_expires_on?.format('YYYY-MM-DD') || null) : null,
      };
      if (sourceType && sourceId) {
        body.source_type = sourceType;
        body.source_id   = sourceId;
      }
      const res = await api.post('/api/compliance', body);
      const newId = res.data.id;
      if (andOpen) {
        if (onCreatedAndOpen) {
          onCreatedAndOpen(newId);
        } else {
          navigate(`/compliance/incidents/${newId}`);
        }
      } else {
        onClose();
        onCreated?.(newId);
      }
    } catch (err) {
      Modal.error({ title: 'Failed to create', content: err.response?.data?.error || 'An error occurred.' });
    } finally {
      setSaving(false);
    }
  };

  const onTeamChange = (val) => {
    setSelectedTeamId(val || null);
    form.setFieldValue('assigned_to_user_id', null);
    loadAssignUsers(val || null);
  };

  const assignableUsers = users; // API already filters to CanEditIncidents >= 2

  return (
    <Modal maskClosable={false}
      title={`Compliance — ${agentName ?? ''}`}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="complete" loading={saving} onClick={() => handleCreate(false)}>Create &amp; Complete</Button>,
        <Button key="open" type="primary" loading={saving} onClick={() => handleCreate(true)}>Create &amp; Open</Button>,
      ]}
      width={780}
      destroyOnClose
    >
      <Divider orientation="left" style={{ fontSize: 13, marginTop: 8 }}>Recent History (90 days)</Divider>
      {historyLoading ? (
        <div style={{ textAlign: 'center', padding: '12px 0' }}><Spin size="small" /></div>
      ) : history.length === 0 ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>No compliance records in the last 90 days.</Typography.Text>
      ) : (
        <Table
          rowKey="ID"
          size="small"
          pagination={false}
          scroll={{ y: 180 }}
          dataSource={history}
          style={{ marginBottom: 4 }}
          columns={[
            { title: 'Date', dataIndex: 'DateOfIncident', key: 'date', width: 90,
              render: (v) => v ? dayjs(v).format('M/D/YY') : '—' },
            { title: 'Type', dataIndex: 'Type', key: 'type', width: 78,
              render: (v) => <Tag style={{ fontSize: 11 }}>{v === 'writeup' ? 'Writeup' : 'Incident'}</Tag> },
            { title: 'Status', dataIndex: 'Status', key: 'status', width: 88,
              render: (v) => <Tag color={STATUS_COLOR[v] ?? 'default'} style={{ fontSize: 11 }}>{STATUS_LABEL[v] ?? v}</Tag> },
            { title: 'Issue Types', dataIndex: 'Issues', key: 'issues',
              render: (v) => v?.length > 0
                ? v.map((name) => <Tag key={name} style={{ fontSize: 11, marginBottom: 2 }}>{name}</Tag>)
                : <Typography.Text type="secondary" style={{ fontSize: 12 }}>—</Typography.Text> },
            { title: '', key: 'link', width: 52,
              render: (_, r) => (
                <Button size="small" type="link" style={{ padding: 0, fontSize: 12 }}
                  onClick={() => navigate(`/compliance/${r.Type === 'writeup' ? 'writeups' : 'incidents'}/${r.ID}`)}>
                  View
                </Button>
              )},
          ]}
        />
      )}

      <Divider orientation="left" style={{ fontSize: 13 }}>Create New Incident</Divider>
      <Form form={form} layout="vertical" onValuesChange={defaultExpiries}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Team" name="team_id" rules={[{ required: true, message: 'Required' }]}>
              <Select
                showSearch allowClear placeholder="Select team…"
                optionFilterProp="label"
                options={teams.map((t) => ({ label: t.Name, value: t.ID }))}
                onChange={onTeamChange}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Assigned To" name="assigned_to_user_id" rules={[{ required: true, message: 'Required' }]}>
              <Select allowClear showSearch optionFilterProp="label" placeholder="Select assignee"
                options={assignableUsers.map((u) => ({ label: u.DisplayName || u.Email, value: u.ID }))} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Date of Incident" name="date_of_incident" rules={[{ required: true, message: 'Required' }]}>
          <DatePicker style={{ width: '100%' }} disabledDate={(d) => d && d.isAfter(dayjs(), 'day')} />
        </Form.Item>
        <Form.Item label="Issue Types" name="issue_ids" rules={[{ required: true, message: 'Required' }]}>
          <Select mode="multiple" placeholder="Select issue types"
            options={issueTypes.map((t) => ({ label: t.Name, value: t.ID }))}
            optionFilterProp="label" />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Warning Number" name="warning_number">
              <Select allowClear options={WARNING_OPTIONS} />
            </Form.Item>
            <Form.Item noStyle dependencies={['warning_number']}>
              {({ getFieldValue }) => getFieldValue('warning_number') ? (
                <Form.Item label="Warning Expires On" name="warning_expires_on"
                  rules={[{ required: true, message: 'Required when a warning is selected' }]}>
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              ) : null}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="QA Related" name="is_qa_related" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item noStyle dependencies={['is_qa_related']}>
              {({ getFieldValue }) => getFieldValue('is_qa_related') ? (
                <Form.Item label="QA Related Expires On" name="qa_related_expires_on"
                  rules={[{ required: true, message: 'Required when QA Related is on' }]}>
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              ) : null}
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Description of Incident" name="description">
          <Input.TextArea rows={3} maxLength={4000} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
}
